import { SelectChangeEvent } from "@mui/material"
import { useLocale } from "next-intl"
import { useState } from "react"
import { extractProperty } from "utils"

export const useFileDeliveryLogic = (
  fileManagementSteps: any,
  allBuildings: any,
  messages: any,
) => {
  const [value, setValue] = useState(0)
  const [selectedBuilding, setSelectedBuilding] = useState<string | null>(null)
  const [filteredFiles, setFilteredFiles] = useState<any[]>([])
  const [filterFilesBuilding, setFilterFilesByBuilding] = useState<any[]>([])
  const [selectedDestination, setDestination] = useState<string | null>(null)
  const [filteredFilesByDestination, setFilteredFilesByDestination] = useState<any[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [groupedFiles, setGroupedFiles] = useState<any[]>([])
  const [hasError, setHasError] = useState(false)

  const locale = useLocale()
  const handleBuildingChange = (event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setHasError(false)
    setSelectedBuilding(event.target.value)
  }

  const handleDestinationChange = (value: string) => {
    setHasError(false)
    setDestination(value)
  }

  const handleCategoriesChange = (event: SelectChangeEvent<typeof selectedCategories>) => {
    const {
      target: { value },
    } = event
    setHasError(false)
    setSelectedCategories(typeof value === "string" ? value.split(",") : value)
  }

  const getSelectedDisplayValues = () => {
    const selectedBuildingName = selectedBuilding
      ? extractProperty(
          allBuildings.find((building: any) => building.id === parseInt(selectedBuilding)),
          "buildingName",
        ) || ""
      : ""

    return {
      building: selectedBuildingName,
      destination: selectedDestination || "",
      categories:
        selectedCategories.length > 0
          ? locale === "he"
            ? `${messages.selected} ${selectedCategories.length} ${messages.categories} `
            : `${selectedCategories.length} ${messages.categories} ${messages.selected}`
          : "",
      files: "",
    }
  }

  const filterFilesByBuilding = () => {
    if (!fileManagementSteps || !selectedBuilding) return []

    return fileManagementSteps.filter((file: any) => {
      const buildingSelection = extractProperty(file, "buildingSelection")?.[0]
      const buildingSelectionValue = extractProperty(buildingSelection, "BuildingSelection")

      if (Array.isArray(buildingSelectionValue)) {
        return buildingSelectionValue.some(
          (building: any) => building.id === parseInt(selectedBuilding),
        )
      }
      return false
    })
  }

  const filterFilesByDestination = (files: any[]) => {
    return files.filter((file: any) => {
      const destination = extractProperty(file, "fileDestination")?.[0]
      const destinationValue = extractProperty(destination, "fileDestination")
      return destinationValue === selectedDestination
    })
  }

  const filterFilesByCategories = (files: any[]) => {
    return files.filter((file: any) => {
      const categories = extractProperty(file, "selectCategory")?.[0]
      const categoriesValue = extractProperty(categories, "selectCategory")

      if (Array.isArray(categoriesValue) && Array.isArray(selectedCategories)) {
        return selectedCategories.some((selectedCategory) =>
          categoriesValue.includes(selectedCategory),
        )
      }
      return false
    })
  }

  const groupFilesByCategory = (files: any[]) => {
    return files.reduce((acc: any, file: any) => {
      const categories = extractProperty(file, "selectCategory")?.[0]
      const categoriesValue = extractProperty(categories, "selectCategory")

      if (Array.isArray(categoriesValue)) {
        // File can be in multiple categories, but only add it to selected categories
        categoriesValue.forEach((category: string) => {
          // Only include this category if it was selected
          if (selectedCategories.includes(category)) {
            if (!acc[category]) {
              acc[category] = []
            }
            acc[category].push(file)
          }
        })
      } else {
        // Fallback: if it's not an array, treat it as a single category
        const category = categoriesValue || "Other"
        // Only include if this category was selected
        if (selectedCategories.includes(category)) {
          if (!acc[category]) {
            acc[category] = []
          }
          acc[category].push(file)
        }
      }

      return acc
    }, {})
  }

  const handleButtonClick = () => {
    switch (value) {
      case 0: {
        if (!selectedBuilding) {
          setHasError(true)
          break
        } else {
          const buildingFiltered = filterFilesByBuilding()
          setFilterFilesByBuilding(buildingFiltered)
          setFilteredFiles(buildingFiltered)
          setValue(1)
          setHasError(false)
        }
        break
      }
      case 1: {
        if (!selectedDestination) {
          setHasError(true)
          break
        } else {
          const destinationFiltered = filterFilesByDestination(filteredFiles)
          setFilteredFilesByDestination(destinationFiltered)
          setFilteredFiles(destinationFiltered)
          setValue(2)
          setHasError(false)
        }
        break
      }
      case 2: {
        if (selectedCategories.length === 0) {
          setHasError(true)
          return
        } else {
          const categoryFiltered = filterFilesByCategories(filteredFiles)
          setFilteredFiles(categoryFiltered)
          const grouped = groupFilesByCategory(categoryFiltered)
          setGroupedFiles(grouped)
          setValue(3)
          setHasError(false)
        }

        break
      }
    }
  }

  const handleGoBack = () => {
    setHasError(false)
    setTimeout(() => {
      switch (value) {
        case 1:
          setDestination(null)
          setValue(0)
          break
        case 2:
          setSelectedCategories([])
          setFilteredFiles(filterFilesBuilding)
          setValue(1)
          break
        case 3:
          // Reset grouped files when going back from file display
          setFilteredFiles(filteredFilesByDestination)
          setGroupedFiles([])
          setValue(2)
          break
      }
    }, 300) // Adjust delay as needed
  }

  const isButtonActive =
    (value === 0 && selectedBuilding) ||
    (value === 1 && selectedDestination) ||
    (value === 2 && selectedCategories.length > 0)

  return {
    // State
    value,
    setValue,
    selectedBuilding,
    filteredFiles,
    selectedDestination,
    selectedCategories,
    groupedFiles,
    hasError,

    // Handlers
    handleBuildingChange,
    handleDestinationChange,
    handleCategoriesChange,
    handleButtonClick,
    handleGoBack,

    // Computed
    getSelectedDisplayValues,
    isButtonActive,
  }
}
